import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  Calendar as CalendarIcon,
  CheckCheck,
  CheckCircle2,
  Layers,
  MessageSquare,
  Receipt,
  Users,
  type LucideIcon,
} from "lucide-react";

import {
  Button,
  Card,
  Chip,
  EmptyState,
  Modal,
  Pagination,
  iconTileToneClass,
  type IconTileTone,
} from "@/components/ui";
import { buildDataTableLabels } from "@/components/table";
import { ErrorState, LoadingState } from "@/components/common";
import { cn } from "@/lib/utils";
import { bookingKeys, bookingService } from "@/services/bookingService";
import type {
  Activity,
  BookingActionCategory,
  BookingCardPage,
  BookingListFilters,
  BookingPageParams,
} from "@/types";
import { ManageBookingActionCard } from "./ManageBookingActionCard";
import { BOOKING_ACTION_CATEGORIES } from "./bookingActionRules";
import { mapBookingToListRow, type BookingListRow } from "./bookingViewModel";
import { FilterChipCount } from "./FilterChipCount";
import { BookingsListCardTitle } from "./BookingsListCardTitle";

export type AttentionActionFilter = BookingActionCategory | "all";

interface BookingsNeedsAttentionSectionProps {
  /** Booking scope (admin seller scope); activity owners pass `{}`. */
  scope: BookingListFilters;
  /** Catalog for payment model / handshake flags on the cards. */
  activityMap: Map<string, Activity>;
  filter: AttentionActionFilter;
  onFilterChange: (filter: AttentionActionFilter) => void;
  bookingsListPath: string;
  adminMode: boolean;
  canInteractHandshake: boolean;
  processingByBookingId: Record<string, boolean>;
  fileInputResetKey: Record<string, number>;
  onReceiptUpload: (booking: BookingListRow, file?: File | null) => void;
  onManualAmountClick?: (bookingId: string, storagePath: string) => void;
  /** Link row under the panel (e.g. "See all bookings" on the dashboard). */
  footerAction?: ReactNode;
  /** Show a loading body while the caller's prerequisites (catalog) load. */
  isLoading?: boolean;
}

const ATTENTION_PREVIEW_LIMIT = 5;
const SHOW_ALL_PAGE_SIZE = 20;

const TILE_CONFIG: Record<
  AttentionActionFilter,
  { icon: LucideIcon; tone: IconTileTone; labelKey: string; chipLabelKey: string }
> = {
  all: {
    icon: Layers,
    tone: "primary",
    labelKey: "actionFilters.all",
    chipLabelKey: "actionFilters.all",
  },
  CONFIRMATION: {
    icon: MessageSquare,
    tone: "accent",
    labelKey: "stats.tile.confirmation",
    chipLabelKey: "actionFilters.confirmation",
  },
  RECEIPT: {
    icon: Receipt,
    tone: "warning",
    labelKey: "stats.tile.receipts",
    chipLabelKey: "actionFilters.receipt",
  },
  COMPLETION: {
    icon: CheckCheck,
    tone: "success",
    labelKey: "stats.tile.completion",
    chipLabelKey: "actionFilters.completion",
  },
};

const TILE_ORDER: readonly AttentionActionFilter[] = [
  "all",
  ...BOOKING_ACTION_CATEGORIES,
];

function actionsForFilter(
  filter: AttentionActionFilter,
): BookingActionCategory[] {
  return filter === "all" ? [...BOOKING_ACTION_CATEGORIES] : [filter];
}

/**
 * "To do" panel on the partner bookings page and dashboard: server-side
 * counters double as filters (to confirm / receipts / completion) above the
 * soonest actionable booking cards; "show all" pages through the rest.
 */
export function BookingsNeedsAttentionSection({
  scope,
  activityMap,
  filter,
  onFilterChange,
  bookingsListPath,
  adminMode,
  canInteractHandshake,
  processingByBookingId,
  fileInputResetKey,
  onReceiptUpload,
  onManualAmountClick,
  footerAction,
  isLoading = false,
}: BookingsNeedsAttentionSectionProps) {
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { t } = useTranslation("bookings");
  const [isShowAllModalOpen, setIsShowAllModalOpen] = useState(false);
  const [showAllPage, setShowAllPage] = useState(1);

  const summaryQuery = useQuery({
    queryKey: bookingKeys.attention(scope),
    queryFn: () => bookingService.getAttention(scope),
  });

  const actionableParams = (skip: number, limit: number): BookingPageParams => ({
    ...scope,
    action: actionsForFilter(filter),
    order: "VISIT_ASC",
    skip,
    limit,
  });
  const previewParams = actionableParams(0, ATTENTION_PREVIEW_LIMIT);
  const previewQuery = useQuery({
    queryKey: bookingKeys.page(previewParams),
    queryFn: () => bookingService.getPage(previewParams),
    placeholderData: keepPreviousData,
  });
  const showAllParams = actionableParams(
    (showAllPage - 1) * SHOW_ALL_PAGE_SIZE,
    SHOW_ALL_PAGE_SIZE,
  );
  const showAllQuery = useQuery({
    queryKey: bookingKeys.page(showAllParams),
    queryFn: () => bookingService.getPage(showAllParams),
    enabled: isShowAllModalOpen,
    placeholderData: keepPreviousData,
  });

  const toRows = (page: BookingCardPage | undefined): BookingListRow[] =>
    (page?.items ?? []).map((booking) =>
      mapBookingToListRow(booking, { activityMap, customerMode: false }),
    );
  const previewRows = toRows(previewQuery.data);
  const showAllRows = toRows(showAllQuery.data);

  const summary = summaryQuery.data;
  const filterCounts = {
    all: summary?.total ?? 0,
    CONFIRMATION: summary?.by_action.CONFIRMATION ?? 0,
    RECEIPT: summary?.by_action.RECEIPT ?? 0,
    COMPLETION: summary?.by_action.COMPLETION ?? 0,
  } satisfies Record<AttentionActionFilter, number>;
  const filteredTotal = previewQuery.data?.total ?? 0;
  const hasMoreThanPreview = filteredTotal > ATTENTION_PREVIEW_LIMIT;
  const hasActionable = filterCounts.all > 0;
  const bodyLoading =
    isLoading || summaryQuery.isLoading || previewQuery.isLoading;
  const loadError = summaryQuery.isError || previewQuery.isError;
  const showAllTotalPages = Math.max(
    1,
    Math.ceil((showAllQuery.data?.total ?? 0) / SHOW_ALL_PAGE_SIZE),
  );

  const changeFilter = (next: AttentionActionFilter) => {
    setShowAllPage(1);
    onFilterChange(next);
  };

  const guestsToday = (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-surface-200 bg-surface-50 px-2 py-1 text-xs font-medium text-surface-600 dark:bg-surface-100/50">
      <Users className="h-3.5 w-3.5 text-primary-600" aria-hidden />
      {t("stats.tile.guests")}
      <span className="font-semibold tabular-nums text-surface-900">
        {summary?.guests_today ?? 0}
      </span>
    </span>
  );

  const filteredEmptyState = (
    <EmptyState
      size="md"
      icon={CalendarIcon}
      title={t("manageModal.emptyTitle")}
      description={t("manageModal.emptyDescription")}
    />
  );

  const renderBookingCards = (list: BookingListRow[]) => (
    <div className="-mx-4 flex flex-col gap-px bg-surface-200 sm:mx-0 sm:gap-2 sm:bg-transparent">
      {list.map((booking) => {
        const latestUploadPath = booking.latestUploadPath;
        return (
          <ManageBookingActionCard
            key={booking.id}
            booking={booking}
            detailPath={`${bookingsListPath}/${booking.id}`}
            adminMode={adminMode}
            canInteractHandshake={canInteractHandshake}
            isProcessingReceipt={processingByBookingId[booking.id] ?? false}
            fileInputResetKey={fileInputResetKey[booking.id] ?? 0}
            onReceiptUpload={(file) => onReceiptUpload(booking, file)}
            onManualAmountClick={
              onManualAmountClick && latestUploadPath
                ? () => onManualAmountClick(booking.id, latestUploadPath)
                : undefined
            }
          />
        );
      })}
    </div>
  );

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
      {!bodyLoading && !loadError && !hasActionable ? (
        <BookingsListCardTitle
          icon={CheckCircle2}
          iconClassName="text-success-600"
          title={t("needsAttention.allClearTitle")}
          subtitle={t("needsAttention.allClearSubtitle")}
          className="text-[15px] font-semibold tracking-tight text-surface-900"
        />
      ) : (
        <BookingsListCardTitle
          icon={AlertCircle}
          iconClassName="text-accent-600"
          title={t("needsAttention.title")}
          subtitle={
            hasActionable
              ? t("needsAttention.subtitle", { count: filterCounts.all })
              : null
          }
          className="text-[15px] font-semibold tracking-tight text-surface-900"
        />
      )}
      {guestsToday}
    </div>
  );

  const tiles = (
    <div
      className="grid grid-cols-2 gap-2 px-4 sm:px-5 @3xl:grid-cols-4"
      role="group"
      aria-label={t("actionFilters.label")}
    >
      {TILE_ORDER.map((value) => {
        const { icon: Icon, tone, labelKey } = TILE_CONFIG[value];
        const selected = filter === value;
        const count = filterCounts[value];
        return (
          <button
            key={value}
            type="button"
            aria-pressed={selected}
            onClick={() => changeFilter(value)}
            className={cn(
              "flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
              selected
                ? "border-primary-300 bg-primary-50/60 ring-1 ring-inset ring-primary-500/20 dark:bg-primary-50/40"
                : "border-surface-200 bg-surface-0 hover:-translate-y-px hover:border-surface-300 hover:shadow-elevated",
            )}
          >
            <span
              className={cn(
                "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
                iconTileToneClass(tone),
              )}
              aria-hidden
            >
              <Icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span
                className={cn(
                  "block text-lg font-semibold leading-tight tabular-nums",
                  count > 0 ? "text-surface-900" : "text-surface-400",
                )}
              >
                {count}
              </span>
              <span className="block text-xs font-medium leading-snug text-surface-500">
                {t(labelKey)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <Card className="@container animate-slide-up overflow-hidden">
        {header}
        {bodyLoading ? (
          <div className="border-t border-surface-200/70">
            <LoadingState message={t("loading")} />
          </div>
        ) : loadError ? (
          <div className="border-t border-surface-200/70">
            <ErrorState
              message={t("errorLoad")}
              onRetry={() => {
                void summaryQuery.refetch();
                void previewQuery.refetch();
              }}
              isRetrying={summaryQuery.isFetching || previewQuery.isFetching}
            />
          </div>
        ) : hasActionable ? (
          <div className="space-y-3 border-t border-surface-200/70 pb-4 pt-3.5">
            {tiles}
            <div className="px-4 sm:px-5">
              {previewRows.length === 0
                ? filteredEmptyState
                : renderBookingCards(previewRows)}
            </div>
            {hasMoreThanPreview ? (
              <div className="flex justify-center px-4 sm:px-5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-primary-600 hover:bg-primary-50 hover:text-primary-700"
                  onClick={() => setIsShowAllModalOpen(true)}
                >
                  {t("needsAttention.showAll", {
                    count: filteredTotal,
                  })}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
        {footerAction ? (
          <div className="flex justify-center border-t border-surface-200/70 px-4 py-2 sm:justify-start sm:px-5">
            {footerAction}
          </div>
        ) : null}
      </Card>

      {isShowAllModalOpen ? (
        <Modal
          isOpen
          onClose={() => setIsShowAllModalOpen(false)}
          closeLabel={tCommon("close")}
          title={t("needsAttention.showAllTitle")}
          description={t("needsAttention.subtitle", {
            count: showAllQuery.data?.total ?? filteredTotal,
          })}
          size="xl"
          mobileFullScreen
        >
          <div className="max-h-[min(70dvh,36rem)] space-y-3 overflow-y-auto">
            <div
              className="overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              role="group"
              aria-label={t("actionFilters.label")}
            >
              <div className="flex w-max min-w-full flex-nowrap gap-2">
                {TILE_ORDER.map((value) => {
                  const selected = filter === value;
                  return (
                    <Chip
                      key={value}
                      selected={selected}
                      onClick={() => changeFilter(value)}
                      className="shrink-0 whitespace-nowrap"
                    >
                      <span>{t(TILE_CONFIG[value].chipLabelKey)}</span>
                      <FilterChipCount
                        count={filterCounts[value]}
                        selected={selected}
                      />
                    </Chip>
                  );
                })}
              </div>
            </div>
            {showAllQuery.isLoading ? (
              <LoadingState message={t("loading")} />
            ) : showAllQuery.isError ? (
              <ErrorState
                message={t("errorLoad")}
                onRetry={() => void showAllQuery.refetch()}
                isRetrying={showAllQuery.isFetching}
              />
            ) : showAllRows.length === 0 ? (
              filteredEmptyState
            ) : (
              renderBookingCards(showAllRows)
            )}
            {showAllTotalPages > 1 ? (
              <Pagination
                labels={tableLabels}
                page={showAllPage}
                totalPages={showAllTotalPages}
                onPageChange={setShowAllPage}
                isLoading={showAllQuery.isFetching}
                total={showAllQuery.data?.total}
                showPageNumbers
              />
            ) : null}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
